import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../../components/Input';
import Btn from '../../../components/ui/Btn';
import Select from '../../../components/Select';
import PermisosStep from './PermisosStep/PermisosStep';
import DraftBanner from '../../../components/DraftBanner';
import { useDraft } from '../../../hooks/useDraft';
import InputFile from '../../../components/InputFile';
import {
  FaProjectDiagram, FaDollarSign, FaCalendarAlt, FaComment,
  FaFolder, FaImage, FaClock, FaTag, FaLink, FaTable,
  FaWhatsapp, FaBrain, FaShieldAlt, FaChevronLeft, FaChevronRight,
  FaSave, FaTimes,
} from 'react-icons/fa';
import { proyectService } from '../../../services/proyectService';
import { proyectTypesService } from '../../../services/proyectTypesService';
import { ProyectManagerService } from '../../../services/ProyectManagerService'
import { attachmentService } from '../../../services/attachmentService';
import { toastError, toastSuccess } from '../../../utils/toast';
import { validateRequired, validateMinLength, validateMaxLength } from '../../../utils/validations';
import { getModuleConfigById } from '../../../utils/moduleConfigs';
import { getDefaultChannels } from '../../../utils/channelsConfig';

const STEPS = [
  { label: 'General',      icon: FaProjectDiagram },
  { label: 'Plataforma',   icon: FaWhatsapp        },
  { label: 'IA & Drive',   icon: FaBrain           },
  { label: 'Suscripción',  icon: FaDollarSign      },
  { label: 'Permisos',     icon: FaShieldAlt       },
];

const AddProyect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEdit, proyectEdit } = location.state || { isEdit: false, proyectEdit: null };

  const [step, setStep] = useState(0);
  const [proyectTypes, setProyectTypes] = useState([]);
  const [agentTypes, setAgentTypes] = useState([]);  // tipos de agente completos (perfil y módulos por defecto)
  const [proyectManagers, setProyectManagers] = useState([]);
  const [isWhatsapp, setIsWhatsapp] = useState(false);

  const [platforms] = useState([
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
  ]);
  const [statusOptions] = useState([
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ]);

  const [proyect, setProyect] = useState({
    name: '', description: '', project_type: '', project_manager_id: '',
    platform: '', status: 'active', token: '', profile_photo: '',
    phone_number_id: '', kb_table: '', drive_link: '', query_name: '',
    google_drive_folder_id: '', last_train: '', message_teplate: '',
    image_template: '', document_template: '', suscription: '', price: '', next_payment: '',
    modules: getModuleConfigById(0),
    channels: getDefaultChannels(),
  });

  const [errors, setErrors] = useState({ name: '', description: '', project_type: '' });

  // Borrador: lo escrito sobrevive si el admin sale de la pantalla. El token nunca se guarda en el navegador.
  const [formLoaded, setFormLoaded] = useState(!isEdit);
  const draftKey = `proyect:${isEdit ? proyectEdit?.id : 'new'}`;
  const draft = useDraft(draftKey, proyect, setProyect, { ready: formLoaded, omit: ['token'] });
  const stepDraft = useDraft(`${draftKey}:step`, step, setStep, { ready: formLoaded });

  useEffect(() => {
    if (isEdit && proyectEdit) {
      const updatedModules = proyectEdit.modules?.map(module => {
        if (module.module === 'chats') {
          const missingKeys = ['can_block_contact', 'can_view_budget_filter', 'can_view_follow_up_filter', 'can_view_notice_filter', 'can_view_generic_filter']
            .filter(key => !module.special_actions.hasOwnProperty(key));
          if (missingKeys.length > 0) {
            const defaults = Object.fromEntries(missingKeys.map(key => [key, false]));
            return { ...module, special_actions: { ...module.special_actions, ...defaults } };
          }
        }
        return module;
      }) || [];
      setProyect({
        name: proyectEdit.name || '',
        description: proyectEdit.description || '',
        project_type: proyectEdit.project_type || '',
        project_manager_id: proyectEdit.project_manager_id || '',
        platform: proyectEdit.platform || '',
        status: proyectEdit.status || 'active',
        token: proyectEdit.token || '',
        profile_photo: proyectEdit.profile_photo || '',
        phone_number_id: proyectEdit.phone_number_id || '',
        kb_table: proyectEdit.kb_table || '',
        drive_link: proyectEdit.drive_link || '',
        query_name: proyectEdit.query_name || '',
        google_drive_folder_id: proyectEdit.google_drive_folder_id || '',
        last_train: proyectEdit.last_train ? proyectEdit.last_train.slice(0, 16) : '',
        message_teplate: proyectEdit.message_teplate || '',
        image_template: proyectEdit.image_template || '',
        document_template: proyectEdit.document_template || '',
        suscription: proyectEdit.suscription || '',
        price: proyectEdit.price || '',
        next_payment: proyectEdit.next_payment ? proyectEdit.next_payment.slice(0, 10) : '',
        modules: updatedModules,
        channels: proyectEdit.channels?.length ? proyectEdit.channels : getDefaultChannels(),
      });
      setIsWhatsapp(proyectEdit.platform === 'whatsapp');
      setFormLoaded(true);
    }
  }, [isEdit, proyectEdit]);

  // El token ya no es legible por SELECT (privilegios de columna); se obtiene vía RPC solo-admin
  const [secretsLoaded, setSecretsLoaded] = useState(false);
  useEffect(() => {
    if (!isEdit || !proyectEdit?.id) return;
    let cancelled = false;
    proyectService.getSecrets(proyectEdit.id)
      .then((secrets) => {
        if (cancelled) return;
        setProyect(prev => ({ ...prev, token: prev.token || secrets?.token || '' }));
        setSecretsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) toastError('No se pudo cargar el token del proyecto');
      });
    return () => { cancelled = true; };
  }, [isEdit, proyectEdit?.id]);

  useEffect(() => {
    const fetchProyectTypes = async () => {
      try {
        const tipos = await proyectTypesService.getAll();
        setAgentTypes(tipos);
        setProyectTypes(tipos.map(t => ({ value: t.id, label: t.name })));
      } catch (error) {
        console.error('Error al obtener tipos de proyectos:', error);
      }
    };
    fetchProyectTypes();
    fetchProyectManagers();
  }, []);

  const fetchProyectManagers = async () => {
    try {
      const managers = await ProyectManagerService.getAll();
      setProyectManagers(managers.map(m => ({ value: m.id, label: m.name })));
    } catch (error) {
      console.error('Error al obtener gerentes:', error);
    }
  };

  const validateStep0 = () => {
    const nameErr = validateRequired(proyect.name)
      || validateMinLength(proyect.name, 3, 'Mínimo 3 caracteres')
      || validateMaxLength(proyect.name, 100, 'Máximo 100 caracteres');
    const descErr = validateRequired(proyect.description)
      || validateMinLength(proyect.description, 3, 'Mínimo 3 caracteres')
      || validateMaxLength(proyect.description, 150, 'Máximo 150 caracteres');
    const typeErr = validateRequired(proyect.project_type);
    setErrors({ name: nameErr || '', description: descErr || '', project_type: typeErr || '' });
    return !nameErr && !descErr && !typeErr;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) {
      toastError('Completa los campos requeridos antes de continuar');
      return;
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handlePrev = () => setStep(s => Math.max(s - 1, 0));

  const resolveProfilePhoto = async () => {
    if (proyect.profile_photo && proyect.profile_photo instanceof File) {
      const response = await attachmentService.uploadImage(proyect.profile_photo);
      if (!response.success) { toastError(response.message); return null; }
      return "https://apidasboard.canteradigital.io/" + response.filePath;
    }
    return proyect.profile_photo;
  };

  const handleSubmit = async () => {
    if (!validateStep0()) { toastError('Revisa los campos requeridos'); return; }
    const photoUrl = await resolveProfilePhoto();
    if (photoUrl === null) return;
    try {
      await proyectService.createProyect({ ...proyect, profile_photo: photoUrl });
      toastSuccess('Proyecto creado correctamente');
      draft.clear(); stepDraft.clear();
      navigate('/proyects');
    } catch {
      toastError('Error al crear el proyecto');
    }
  };

  const handleEdit = async () => {
    if (!validateStep0()) { toastError('Revisa los campos requeridos'); return; }
    const photoUrl = await resolveProfilePhoto();
    if (photoUrl === null) return;
    try {
      // Si el token no se pudo cargar y no se capturó uno nuevo, no se envía (evita borrarlo)
      const tokenToSave = (secretsLoaded || proyect.token) ? proyect.token : undefined;
      await proyectService.updateProyect(proyectEdit.id, { ...proyect, token: tokenToSave, profile_photo: photoUrl });
      toastSuccess('Proyecto editado correctamente');
      draft.clear(); stepDraft.clear();
      navigate('/proyects');
    } catch {
      toastError('Error al editar el proyecto');
    }
  };

  const isLast = step === STEPS.length - 1;

  return (
    <div className="max-w-6xl mx-auto rounded-[28px] bg-panel ring-1 ring-line" style={{ boxShadow: 'var(--shadow)' }}>
      <div className="sticky top-0 z-20 rounded-t-[28px] bg-panel/95 backdrop-blur border-b border-line px-5 sm:px-7 pt-6 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-[26px] sm:text-[30px] leading-tight font-bold text-fg">{isEdit ? `Editar ${proyect.name || 'proyecto'}` : 'Nuevo proyecto'}</h1>
          <div className="flex items-center gap-2">
            <Btn variant="ghost" icon={FaTimes} onClick={() => navigate('/proyects')}>Cancelar</Btn>
            {step > 0 && <Btn icon={FaChevronLeft} onClick={handlePrev}>Anterior</Btn>}
            {!isLast && <Btn onClick={handleNext}>Siguiente <FaChevronRight aria-hidden className="h-3 w-3" /></Btn>}
            <Btn variant="primary" icon={FaSave} onClick={isEdit ? handleEdit : handleSubmit}>{isEdit ? 'Guardar cambios' : 'Crear proyecto'}</Btn>
          </div>
        </div>
        <ol className="mt-5 flex gap-1.5 overflow-x-auto [scrollbar-width:none]" aria-label="Pasos">
          {STEPS.map((s, i) => {
            const done = i < step;
            const current = i === step;
            const Icon = s.icon;
            return (
              <li key={i} className="shrink-0">
                <button type="button" onClick={() => setStep(i)} aria-current={current ? 'step' : undefined}
                  className={`inline-flex items-center gap-2 h-9 pl-1.5 pr-3.5 rounded-full text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70
                    ${current ? 'bg-fg text-ink' : done ? 'bg-live-soft text-fg hover:bg-live/20' : 'bg-raised text-fg-dim hover:text-fg'}`}>
                  <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] ${current ? 'bg-ink/15' : done ? 'bg-live text-ink' : 'bg-panel'}`}>
                    {done ? '✓' : <Icon aria-hidden size={11} />}
                  </span>
                  {s.label}
                  {done && <span className="sr-only"> (listo)</span>}
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {/* ── Contenido del step ── */}
      <div className="p-5 sm:p-7">
        <div className="mb-5 empty:hidden">
          <DraftBanner restoredAt={draft.restoredAt} onDiscard={() => { draft.discard(); stepDraft.discard(); }} />
        </div>

        {/* Step 0 — General */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <p className="font-display text-[17px] font-bold text-fg mb-4">
                Información básica del proyecto
              </p>
              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Nombre del Proyecto" name="name" type="text" required size="lg"
                  value={proyect.name} leftIcon={<FaProjectDiagram />}
                  onChange={(e) => { setProyect({ ...proyect, name: e.target.value }); }}
                  error={errors.name}
                />
                <Input
                  label="Descripción" name="description" type="text" required size="lg"
                  value={proyect.description} leftIcon={<FaProjectDiagram />}
                  onChange={(e) => setProyect({ ...proyect, description: e.target.value })}
                  error={errors.description}
                />
                <InputFile
                  label="Imagen de portada" name="image"
                  placeholder="imagen de portada"
                  value={proyect.profile_photo}
                  onChange={(file) => setProyect(prev => ({ ...prev, profile_photo: file }))}
                  error={errors.profile_photo}
                />
              </div>
            </div>

            <div>
              <p className="font-display text-[17px] font-bold text-fg mb-4">
                Configuración
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  id="project_type_id" name="project_type_id" label="Tipo de Proyecto" required
                  options={proyectTypes} value={proyect.project_type}
                  onChange={(e) => {
                    const newTypeId = e.target.value;
                    // Módulos con que nace el proyecto: los del tipo de agente (Tipos de agente) o la config base
                    const type = agentTypes.find(t => String(t.id) === String(newTypeId));
                    setProyect({ ...proyect, project_type: newTypeId, modules: type?.default_modules || getModuleConfigById(newTypeId) });
                  }}
                  placeholder="Selecciona un tipo" isSearchable error={errors.project_type}
                />
                <Select
                  id="project_manager_id" name="project_manager_id" label="Gerente de Proyecto"
                  options={proyectManagers} value={proyect.project_manager_id}
                  onChange={(e) => setProyect({ ...proyect, project_manager_id: e.target.value })}
                  placeholder="Selecciona un gerente" isSearchable={false}
                />
                <Select
                  id="platform" name="platform" label="Plataforma de Mensajería"
                  options={platforms} value={proyect.platform}
                  onChange={(e) => {
                    setProyect({ ...proyect, platform: e.target.value });
                    setIsWhatsapp(e.target.value === 'whatsapp');
                  }}
                  placeholder="Selecciona una plataforma" isSearchable={false}
                />
                <Select
                  id="status" name="status" label="Estado del Proyecto"
                  options={statusOptions} value={proyect.status}
                  onChange={(e) => setProyect({ ...proyect, status: e.target.value })}
                  placeholder="Selecciona un estado" isSearchable={false}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 1 — Plataforma */}
        {step === 1 && (
          <div className="space-y-6">
            {isWhatsapp ? (
              <>
                <div>
                  <p className="font-display text-[17px] font-bold text-fg mb-4">
                    Credenciales WhatsApp Business
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Token de Acceso" name="token" type="text" required size="lg"
                      value={proyect.token} leftIcon={<FaWhatsapp />}
                      onChange={(e) => setProyect({ ...proyect, token: e.target.value })}
                    />
                    <Input
                      label="Phone Number ID" name="phone_number_id" type="text" required size="lg"
                      value={proyect.phone_number_id} leftIcon={<FaWhatsapp />}
                      onChange={(e) => setProyect({ ...proyect, phone_number_id: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <p className="font-display text-[17px] font-bold text-fg mb-4">
                    Plantillas de Mensajes Meta
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Nombre de Plantilla de Texto" name="message_teplate" type="text" size="lg"
                      value={proyect.message_teplate} leftIcon={<FaComment />}
                      onChange={(e) => setProyect({ ...proyect, message_teplate: e.target.value })}
                      placeholder="Ej: bienvenida_agente"
                    />
                    <Input
                      label="Nombre de Plantilla de Imagen" name="image_template" type="text" size="lg"
                      value={proyect.image_template} leftIcon={<FaImage />}
                      onChange={(e) => setProyect({ ...proyect, image_template: e.target.value })}
                      placeholder="Ej: envio_imagenes"
                    />
                    <Input
                      label="Nombre de Plantilla de Documento" name="document_template" type="text" size="lg"
                      value={proyect.document_template} leftIcon={<FaFolder />}
                      onChange={(e) => setProyect({ ...proyect, document_template: e.target.value })}
                      placeholder="Ej: envio_documentos"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-16 rounded-xl"
                style={{ backgroundColor: 'rgb(var(--raised))', border: '1px solid rgb(var(--fg) / 0.06)' }}
              >
                <FaWhatsapp size={32} style={{ color: 'rgb(var(--fg-faint))', marginBottom: 12 }} />
                <p className="text-sm" style={{ color: 'rgb(var(--fg-faint))' }}>
                  Esta sección aplica solo cuando la plataforma es <strong>WhatsApp</strong>.
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgb(var(--fg-faint))' }}>
                  Regresa al paso General para seleccionar la plataforma.
                </p>
              </div>
            )}

            {/* Google Drive — siempre visible */}
            <div>
              <p className="font-display text-[17px] font-bold text-fg mb-4">
                Integración con Google Drive
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Carpeta Google Drive (ID)" name="google_drive_folder_id" type="text" size="lg"
                  value={proyect.google_drive_folder_id} leftIcon={<FaFolder />}
                  onChange={(e) => setProyect({ ...proyect, google_drive_folder_id: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — IA & Drive */}
        {step === 2 && (
          <div>
            <p className="font-display text-[17px] font-bold text-fg mb-4">
              Base de conocimientos y configuración del agente IA
            </p>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Tabla de Base de Conocimientos" name="kb_table" type="text" size="lg"
                value={proyect.kb_table} leftIcon={<FaTable />}
                onChange={(e) => setProyect({ ...proyect, kb_table: e.target.value })}
              />
              <Input
                label="Enlace de Drive (URL)" name="drive_link" type="text" size="lg"
                value={proyect.drive_link} leftIcon={<FaLink />}
                onChange={(e) => setProyect({ ...proyect, drive_link: e.target.value })}
              />
              <Input
                label="Nombre de Función de Query" name="query_name" type="text" size="lg"
                value={proyect.query_name} leftIcon={<FaProjectDiagram />}
                onChange={(e) => setProyect({ ...proyect, query_name: e.target.value })}
              />
              <Input
                label="Última Capacitación IA" name="last_train" type="datetime-local" size="lg"
                value={proyect.last_train} leftIcon={<FaClock />}
                onChange={(e) => setProyect({ ...proyect, last_train: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 3 — Suscripción */}
        {step === 3 && (
          <div>
            <p className="font-display text-[17px] font-bold text-fg mb-4">
              Plan contratado y facturación
            </p>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Tipo de Suscripción" name="suscription" type="text" size="lg"
                value={proyect.suscription} leftIcon={<FaTag />}
                onChange={(e) => setProyect({ ...proyect, suscription: e.target.value })}
                placeholder="Ej: Básico, Pro, Enterprise"
              />
              <Input
                label="Precio (MXN)" name="price" type="text" size="lg"
                value={proyect.price} leftIcon={<FaDollarSign />}
                onChange={(e) => setProyect({ ...proyect, price: e.target.value })}
                placeholder="Ej: 2500"
              />
              <Input
                label="Próximo Pago" name="next_payment" type="date" size="lg"
                value={proyect.next_payment} leftIcon={<FaCalendarAlt />}
                onChange={(e) => setProyect({ ...proyect, next_payment: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 4 — Permisos */}
        {step === 4 && (
          <PermisosStep
            proyect={proyect}
            setProyect={setProyect}
            projectId={isEdit ? proyectEdit?.id : null}
            agentType={agentTypes.find(t => String(t.id) === String(proyect.project_type))}
          />
        )}

      </div>
    </div>
  );
};

export default AddProyect;
